// `/admin/withdrawals/$id` (croqui `screens/admin/saques.tsx`, frame `DetalheSaque`).
//
// Este componente é o CONTEÚDO do `RouteDrawer` da rota-pai: o cabeçalho (título, id e o
// botão de fechar) é do drawer, e a fila continua desenhada embaixo, inclusive em deep
// link.
//
// Approve, reject e mark-failed devolvem `403 mfa_required` sem step-up recente e o
// `StepUpDialog` de `api/client.ts` resolve sozinho (`spec/17` §4.3). O GET é a exceção:
// ele **não** falha sem step-up, só entrega a chave mascarada (`spec/12` §4.2), então
// "ver chave" pede o step-up por `useStepUp()` e recarrega o detalhe.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import {
  type AdminWithdrawalDetail,
  adminWithdrawalQueryOptions,
  approveWithdrawal,
  markWithdrawalFailed,
  pollWithdrawal,
  rejectWithdrawal,
} from '../api/queries/admin-withdrawals.ts';
import { useStepUp } from '../app/step-up.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Empty } from '../components/kit/empty.tsx';
import {
  type AdminWithdrawalAction,
  AdminWithdrawalDetailView,
} from '../features/admin-withdrawals/withdrawal-detail.tsx';
import { isNotFound } from '../features/withdrawals/errors.ts';

interface ActionInput {
  action: AdminWithdrawalAction;
  reason?: string;
  confirm?: string;
}

function runAction(id: string, input: ActionInput): Promise<unknown> {
  switch (input.action) {
    case 'approve':
      return approveWithdrawal(id);
    case 'reject':
      return rejectWithdrawal(id, { reason: input.reason ?? '' });
    case 'poll':
      return pollWithdrawal(id);
    case 'mark-failed':
      return markWithdrawalFailed(id, {
        reason: input.reason ?? '',
        confirm: input.confirm ?? '',
      });
  }
}

function AdminWithdrawalPage() {
  const { id } = Route.useParams();
  // O `search` é da rota-PAI (a aba e o recorte por seller): trocar de saque dentro do
  // drawer não pode apagar o filtro da fila que ficou embaixo.
  const search = useSearch({ from: '/admin/withdrawals' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const requestStepUp = useStepUp();
  const [revealing, setRevealing] = useState(false);

  const detail = useQuery(adminWithdrawalQueryOptions(id));

  /**
   * Uma mutação só para as quatro ações: todas terminam igual (invalidar a fila e
   * recarregar o detalhe) e o `invalid_state` da API precisa aparecer com o estado já
   * atualizado, senão o drawer mostra um erro ao lado de um status velho.
   */
  const act = useMutation({
    mutationFn: (input: ActionInput) => runAction(id, input),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
  });

  const reveal = async (): Promise<void> => {
    setRevealing(true);
    try {
      if (await requestStepUp()) {
        await detail.refetch();
      }
    } finally {
      setRevealing(false);
    }
  };

  if (detail.isPending) {
    return <CardSkeleton lines={5} />;
  }

  // 404 pelo `code`, nunca pelo texto do status (`spec/17` §4.3 item 1).
  if (isNotFound(detail.error)) {
    return (
      <Empty
        icon="wallet"
        title="Saque não encontrado"
        hint="Este saque não existe ou foi apagado. Confira o id na fila."
      />
    );
  }

  if (detail.error !== null || detail.data === undefined) {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  return (
    <AdminWithdrawalDetailView
      detail={detail.data satisfies AdminWithdrawalDetail}
      pending={act.isPending ? act.variables.action : undefined}
      actionError={act.error}
      revealing={revealing}
      onApprove={() => {
        act.mutate({ action: 'approve' });
      }}
      onReject={(reason) => {
        act.mutate({ action: 'reject', reason });
      }}
      onPoll={() => {
        act.mutate({ action: 'poll' });
      }}
      onMarkFailed={(body) => {
        act.mutate({ action: 'mark-failed', ...body });
      }}
      onReveal={() => {
        void reveal();
      }}
      onSelectHistory={(withdrawal) => {
        void navigate({
          to: '/admin/withdrawals/$id',
          params: { id: withdrawal.id },
          search,
        });
      }}
      onSelectSeller={(sellerId) => {
        // `status: 'all'` porque a pergunta aqui é "o que esse seller já sacou", não
        // "o que dele está na fila".
        void navigate({
          to: '/admin/withdrawals',
          search: { status: 'all', seller_id: sellerId },
        });
      }}
    />
  );
}

export const Route = createFileRoute('/admin/withdrawals/$id')({ component: AdminWithdrawalPage });
